/**
 * useIsShortViewport — reactive check for a viewport with no vertical room.
 *
 * The axis the avatar dock's presenter is actually bound by when a phone is
 * turned sideways. Orientation is the wrong proxy: a modern phone in landscape
 * is 812–892px wide, which is the OVERLAY tier, so an orientation test aimed at
 * `fullscreen` only ever caught an SE. What every one of them has in common is
 * HEIGHT — 390–430px — and the dock is bottom-anchored, so 240px of presenter
 * plus the bar's own 56px row and the 16px inset leaves the bubble beside it
 * nothing. See {@link ../components/websiteAgentLayout#compactAvatarPresenterSize}.
 *
 * Same discipline as {@link ./useIsNarrowPhone}: reach for it when the branch
 * has to happen in JS/TS. A purely visual change belongs in a CSS `@media` rule.
 */

import { hasMatchMedia, realMatchMedia } from '../sdk/native-viewport';
import { useMediaQueryFlag } from './useMediaQueryFlag';

/**
 * Above every phone in landscape (430px on the largest) and below every tablet
 * upright (1024px on the smallest), so nothing that has room to spare is caught.
 *
 * Known and accepted: on a host page whose viewport meta asks for
 * `interactive-widget=resizes-content` — and on older WebViews, which do it
 * unconditionally — opening the soft keyboard shrinks the LAYOUT viewport, so
 * an upright phone can cross this line while the visitor types and the
 * presenter shrinks under them. Modern Chrome and Safari resize only the VISUAL
 * viewport by default, which media queries do not see, so the frame holds on
 * the pages we actually run on. No hysteresis: a timer that held the old frame
 * would also hold a stale one through a real rotation, which is the failure
 * that matters more.
 */
export const SHORT_VIEWPORT_MAX_HEIGHT_PX = 480;

/** The media query this hook is defined by. */
export const SHORT_VIEWPORT_MEDIA_QUERY = `(max-height: ${SHORT_VIEWPORT_MAX_HEIGHT_PX}px)`;

/**
 * Synchronous, one-shot read of the current match. Safe to call during
 * render — returns `false` when there's no `window`/`matchMedia` (SSR or a
 * non-DOM test env), which is the roomy default every caller wants. Also the
 * hook's lazy initial state, so the first paint is correct.
 */
export function matchesShortViewport(): boolean {
  if (!hasMatchMedia()) return false;
  return !!realMatchMedia(SHORT_VIEWPORT_MEDIA_QUERY)?.matches;
}

/**
 * Returns `true` while the viewport is 480px tall or less, and updates live as
 * the device rotates.
 */
export function useIsShortViewport(): boolean {
  return useMediaQueryFlag(SHORT_VIEWPORT_MEDIA_QUERY, matchesShortViewport);
}
